import React from 'react';
import * as Label from "@radix-ui/react-label";
import { motion } from "framer-motion";
import { useTheme } from "../Theme/ThemeProvider";
import { 
  PaperPlaneIcon, 
  FileIcon,
  Cross2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon
} from "@radix-ui/react-icons";
import { ReactComponent as ReasoningIconSVG } from '../Icon/ReasoningIcon.svg';
import { ReactComponent as DataAnalysisIconSVG } from '../Icon/DataAnalysisIcon.svg';
import { ReactComponent as FileUploadIconSVG } from '../Icon/FileUploadIcon.svg';
import { MdTranslate } from "react-icons/md";
import { TbWorldSearch } from 'react-icons/tb';
import { PiImage } from 'react-icons/pi';
import { AudioLines, Mic, MicOff, X, Check } from 'lucide-react';
import { useWhisperDictation } from '../../hooks/useWhisperDictation';
import { dictationService } from '../../utils/voice/dictation.service';
import { DictationWaveform } from './input/controls/DictationWaveform';
import { IconType } from 'react-icons';
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PreviewableFileAttachment } from './response/PreviewableFileAttachment';
import { AudioWaveform } from '../VoiceMode/AudioWaveform';
import { VoiceCircle } from '../VoiceMode/VoiceCircle';
import { BsFillStopFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { clearUploadedFiles, ensureSessionId, clearTargetLanguages, setLastTranslation, setIsTranslating, setTranslationQuota, setTranslationHistory, addTranslationMessage, setTranslationError } from '../../store/chatSlice';
import { PLUS_MENU_STEP_IDS } from '../../store/tutorialSlice';
import type { ChatModeState } from '../../store/chatModeSlice';
import { setSelectedTool, setSelectedModel } from '../../store/chatModeSlice';
import { FileUploadDialog, FileSelectionWarningDialog } from '../Dialogs/FileUploadDialog';
import { hideGlobalBlocking } from '../../store/loadingSlice';
import { useMsal } from '@azure/msal-react';
import { createDocumentTranslationService } from '../../utils/document/documentTranslation.service';
import { buildTranslationMessageContent } from '../../utils/document/translationMessageContent';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { MicrophonePermissionDialog } from '../Dialogs/MicrophonePermissionDialog';
import { MicrophoneSelectorDialog } from '../Dialogs/MicrophoneSelectorDialog';
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import logger from "../../utils/logger/logger";
import { ILanguageOption } from './translation/LanguageSelector';
import { TranslationMode } from './translation/TranslationMode';
import { InactivityWarningDialog } from '../Dialogs/InactivityWarningDialog';
import { useVoiceModeHandlers } from '../../hooks/useVoiceModeHandlers';
import { authSelector } from "../../features/auth/authSlice";
import { isModelSelectorVisible, getToolForModelSelection, getVisibleModelOptions, MODEL_OPTIONS, ModelConfig } from '../../utils/config/modelConfig';
import { getRagPlaceholder } from '../../utils/rag/ragConfig';
import { useChatInputForm } from '../../hooks/useChatInputForm';
import { useChatFileUpload } from '../../hooks/useChatFileUpload';
import { useChatToolSelection } from '../../hooks/useChatToolSelection';

// Wrapper for ReasoningIcon SVG — maps `size` to width/height for consistency with react-icons
const ReasoningModelIcon: React.FC<{ size?: number; width?: number; height?: number; className?: string; style?: React.CSSProperties }> = ({ size = 18, width, height, className, style }) => (
  <ReasoningIconSVG width={width ?? size} height={height ?? size} className={className} style={style} />
);

const DataAnalysisIcon: React.FC<{ size?: number; width?: number; height?: number; className?: string; style?: React.CSSProperties }> = ({ size = 18, width, height, className, style }) => (
  <DataAnalysisIconSVG width={width ?? size} height={height ?? size} className={className} style={style} />
);

const FileUploadIcon: React.FC<{ size?: number; width?: number; height?: number; className?: string; style?: React.CSSProperties }> = ({ size = 18, width, height, className, style }) => (
  <FileUploadIconSVG width={width ?? size} height={height ?? size} className={className} style={style} />
);

// Wrapper to ensure a valid JSX.Element type using React.createElement
const StopIcon: React.FC<{ size?: number; color?: string }> = ({ size = 28, color = '#D10411' }) => {
  const icon = React.createElement(BsFillStopFill as any, { size, color });
  return React.createElement('span', { style: { display: 'inline-flex', lineHeight: 0 } }, icon) as React.ReactElement;
};

interface ChatOption {
  label: string;
  icon: React.ComponentType<any> | IconType;
  apiLabel: string;
}

interface ChatInputProps {
  onHeightChange?: (height: number) => void;
  onNavigateToChat?: (message: string, tools?: string[]) => void;
  onAddMessage?: (message: any) => void;
  isStreaming?: boolean;
  onStopStream?: () => void;
  chatPage?: boolean;
  initialTools?: string[];
  isImageStreaming?: boolean; // specifically track image generation streaming state
  error?: Error | null; // propagate error state to adjust generating UI
  externalValue?: string; // external value to set in the input
  onExternalValueApplied?: () => void; // callback after external value is applied
  onClearChat?: () => void; // callback to clear chat history when switching tools
  hasMessages?: boolean; // whether chat has any messages
  lastConversationTool?: string | null;
  iconSize?: number;
  isWideScreen?: boolean; // from ChatPage, true if above threshold
}

/** Aborts every active FileReader in the ref array and empties the array. */
function abortAndClearReaders(readersRef: React.RefObject<FileReader[]>) {
  readersRef.current.forEach(r => r.abort());
  readersRef.current = [];
}

export function ChatInput({ onHeightChange, onNavigateToChat, onAddMessage: _onAddMessage, isStreaming, onStopStream, chatPage, initialTools, isImageStreaming, error, externalValue, onExternalValueApplied, onClearChat, hasMessages, lastConversationTool, iconSize, isWideScreen }: ChatInputProps) {
  // Place chatModeState selector at the very top so it's available everywhere
  const chatModeState: ChatModeState = useSelector((state: RootState) => state.chatMode);
  const selectedTool = chatModeState.selectedTool;
  // Track if Summarize mode is active (visual indicator and auto-filled prompt)
  const [isSummarizeActive, setIsSummarizeActive] = React.useState(false);
  const uploadedFiles = useSelector((state: RootState) => state.chat.uploadedFiles);
  const [isFileUploadActive, setIsFileUploadActive] = React.useState(false);

  const prevModeRef = React.useRef(chatModeState.mode);
  const prevRagChatTypeRef = React.useRef(chatModeState.ragChatType);
  const prevSelectedModelIdRef = React.useRef(chatModeState.selectedModelId);
  const dispatch = useDispatch();

  // Clear uploaded files whenever the user switches to an image model
  React.useEffect(() => {
    const prevId = prevSelectedModelIdRef.current;
    prevSelectedModelIdRef.current = chatModeState.selectedModelId;
    if (prevId === chatModeState.selectedModelId) return;
    if (getToolForModelSelection(chatModeState.selectedModelId) === 'image_generation') {
      if (uploadedFiles.length > 0) {
        dispatch(clearUploadedFiles());
      }
      setIsFileUploadActive(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatModeState.selectedModelId, dispatch]);

  // Clear selectedTool whenever the chat mode or RAG type actually changes (skip initial mount)
  React.useEffect(() => {
    const modeChanged = prevModeRef.current !== chatModeState.mode;
    const ragChanged = prevRagChatTypeRef.current !== chatModeState.ragChatType;
    prevModeRef.current = chatModeState.mode;
    prevRagChatTypeRef.current = chatModeState.ragChatType;

    if ((modeChanged || ragChanged) && selectedTool !== null) {
      dispatch(setSelectedTool(null));
      setIsTranslationCollapsed(false);
    }
  }, [chatModeState.mode, chatModeState.ragChatType]); // eslint-disable-line react-hooks/exhaustive-deps
  const auth = useSelector(authSelector);
  const previousToolRef = React.useRef<string | null>(null); // Track previous tool for clearing chat on switch
  const [toolTextWidth, setToolTextWidth] = useState(80);
  const { accent, mode } = useTheme();
  // Track first tool used to determine which tools to show/hide
  const [firstToolUsed, setFirstToolUsed] = useState<string | null>(null);
  // Dropdown removed; tools now always visible as bubbles.
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);
  const mobileImageInputRef = React.useRef<HTMLInputElement>(null);
  const bottomBubbleContainerRef = React.useRef<HTMLDivElement>(null);

  // (conversationStarted no longer gates thumbnails; removed selector to avoid unused lint warning)
  const sessionId = useSelector((state: RootState) => state.chat.sessionId);
  const globalBlocking = useSelector((state: RootState) => state.loading.globalBlocking);
  const { instance: msalInstance } = useMsal();

  // Speech-to-text via Azure Whisper (server-side, works on ALL browsers)
  const {
    isListening: isSpeechListening,
    transcript: speechTranscript,
    isTranscribing: isSpeechTranscribing,
    isStarting: isSpeechStarting,
    error: speechError,
    audioLevel: speechAudioLevel,
    frequencyData: speechFrequencyData,
    startListening: startSpeech,
    stopListening: stopSpeech,
    confirmDictation,
    cancelDictation,
    resetTranscript: resetSpeech,
    mode: dictationMode,
  } = useWhisperDictation();
  // Snapshot of textarea value captured at the moment dictation starts.
  // Live-preview is rendered as: preDictationValueRef + speechTranscript
  // directly inside the textarea value prop — not via placeholder — so it is
  // always visible regardless of whether the textarea already has content.
  const preDictationValueRef = React.useRef<string>('');
  const voiceModeState = useSelector((state: RootState) => state.voiceMode);
  const { isActive: isVoiceModeActive, isListening: isVoiceModeListening, isPaused: isVoiceModePaused, isRestarting: isVoiceModeRestarting, showInactivityWarning, inactivityCountdown, disconnectedByInactivity, showNoAudioDialog } = voiceModeState;

  // Voice mode handlers (loading state, dialogs, start/stop/pause, mic selector, etc.)
  const {
    voiceModeLoading,
    audioLevel,
    frequencyData,
    showMicPermissionDialog,
    setShowMicPermissionDialog,
    showVoiceModeError,
    setShowVoiceModeError,
    voiceModeErrorMessage,
    showMicSelector,
    setShowMicSelector,
    handleVoiceModeButtonClick,
    handleDismissInactivityWarning,
    handleVoiceModePauseToggle,
    handleVoiceModeStop,
    handleMicrophoneSelected,
    handleNoAudioDialogConfirm,
    handleNoAudioDialogDismiss,
  } = useVoiceModeHandlers({ onNavigateToChat, chatPage });

  const normalizeConversationTool = React.useCallback((tool: string | null | undefined) => {
    return tool || 'regular_chat';
  }, []);

  const shouldClearForTargetTool = React.useCallback((targetTool: string | null | undefined) => {
    if (!hasMessages) {
      return false;
    }

    return normalizeConversationTool(lastConversationTool) !== normalizeConversationTool(targetTool);
  }, [hasMessages, lastConversationTool, normalizeConversationTool]);

  // Dictation error dialog state
  const [showDictationError, setShowDictationError] = React.useState(false);

  // Check if tools should be hidden
  // - Always hide for 3M Knowledge mode when any RAG is selected
  // - Never hide when mode is 'Ask Chat', regardless of the remembered ragChatType
  const shouldHideTools = chatModeState.mode === '3M Knowledge' && chatModeState.ragChatType !== null;

  // Dynamic placeholder based on RAG type
  const getPlaceholder = () => {
    // Show standard placeholder for any selected tool (never for compare)
    if (selectedTool) {
      if (selectedTool === 'reasoning') {
        const selectedModel = MODEL_OPTIONS.find((m: ModelConfig) => m.id === chatModeState.selectedModelId);
        const modelName = selectedModel?.displayName.split(' - ')[0] || "Reasoning";
        return `Using ${modelName} Reasoning Model...`;
      }
      if(selectedTool === 'image_generation') {
        const selectedModel = MODEL_OPTIONS.find((m: ModelConfig) => m.id === chatModeState.selectedModelId);
        const modelName = selectedModel?.displayName.split(' - ')[0] || "Image Generation";
        return `Using ${modelName} Model...`;
      }
      return `Using ${getToolDisplayName(selectedTool)}...`;
    }
    
    if (isMobile) {
      return "Say something...";
    }
    
    // Customize placeholder for different RAG types — only when actually in 3M Knowledge mode
    if (chatModeState.mode === '3M Knowledge') {
      const ragPlaceholder = getRagPlaceholder(chatModeState.ragChatType);
      if (ragPlaceholder) return ragPlaceholder;
    }
    
    return "How can I help you today?";
  };

  // Note: File clearing when switching to 3M Knowledge is now handled by Header.tsx with confirmation dialog

  // Mobile tools menu state
  const [isMobileToolsMenuOpen, setIsMobileToolsMenuOpen] = React.useState(false);
  const [isMobileAiModelPanelOpen, setIsMobileAiModelPanelOpen] = React.useState(false);
  // Desktop plus menu state (secondary tools dropdown)
  const [isDesktopPlusMenuOpen, setIsDesktopPlusMenuOpen] = React.useState(false);
  const desktopPlusButtonRef = React.useRef<HTMLButtonElement>(null);
  const [desktopPlusMenuPos, setDesktopPlusMenuPos] = React.useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const [isDesktopAiModelSubPanelOpen, setIsDesktopAiModelSubPanelOpen] = React.useState(false);
  const [desktopAiModelSubPanelPos, setDesktopAiModelSubPanelPos] = React.useState<{ top: number; left: number } | null>(null);
  const desktopAiModelButtonRef = React.useRef<HTMLButtonElement>(null);

  // Tutorial awareness: auto-open plus menu for steps whose targets live inside it
  const tutorialIsActive = useSelector((state: RootState) => state.tutorial.isActive);
  const tutorialCurrentStepId = useSelector((state: RootState) => {
    const { steps, currentStepIndex } = state.tutorial;
    return steps[currentStepIndex]?.id ?? null;
  });
  const isTutorialOnPlusMenuStep = tutorialIsActive && tutorialCurrentStepId !== null && PLUS_MENU_STEP_IDS.has(tutorialCurrentStepId);

  // Open / close the plus menu automatically when the tutorial navigates to / away from plus-menu steps
  React.useEffect(() => {
    if (isTutorialOnPlusMenuStep) {
      if (!isDesktopPlusMenuOpen && desktopPlusButtonRef.current) {
        const rect = desktopPlusButtonRef.current.getBoundingClientRect();
        const dropdownHeight = 140;
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < dropdownHeight + 8) {
          setDesktopPlusMenuPos({ bottom: window.innerHeight - rect.top + 6, left: rect.left });
        } else {
          setDesktopPlusMenuPos({ top: rect.bottom + 6, left: rect.left });
        }
        setIsDesktopPlusMenuOpen(true);
      }
    } else if (tutorialIsActive && isDesktopPlusMenuOpen) {
      // Tutorial moved to a step outside the plus menu — close it
      setIsDesktopPlusMenuOpen(false);
    }
  }, [isTutorialOnPlusMenuStep, tutorialIsActive]); // eslint-disable-line react-hooks/exhaustive-deps
  // Track whether File Upload was selected from '+' menu (pill visible immediately)
  // (removed duplicate declaration)

  // Collapsible file preview state (only for chatPage)
  const [isFilePreviewCollapsed, setIsFilePreviewCollapsed] = React.useState(false);

  // Track if Compare mode is active (visual indicator and auto-filled prompt)
  const [isCompareActive, setIsCompareActive] = React.useState(false);

    // Track if translation has completed to prevent placeholder from being added
  const translationCompletedRef = React.useRef(false);
  const translationFileInputRef = React.useRef<HTMLInputElement>(null);
  // Collapse/expand state for translation mode
  const [isTranslationCollapsed, setIsTranslationCollapsed] = React.useState(false);
  // Track last translation request to detect changes - using Redux for persistence across navigation
  const {lastTranslation, targetLanguages, glossaryFiles, translationHistory} = useSelector((state: RootState) => state.chat);


  // Track tool switching - removed automatic clearing, will be handled with confirmation
  React.useEffect(() => {
    if (!selectedTool || !previousToolRef.current) {
      previousToolRef.current = selectedTool;
      return;
    }
    
    previousToolRef.current = selectedTool;
  }, [selectedTool]);
  // File upload dialog state
  const [isFileUploadDialogOpen, setIsFileUploadDialogOpen] = useState(false);

  // Device detection using custom hook
  const deviceInfo = useDeviceDetection();
  const { isMobile, isTablet } = deviceInfo;
  // Determine if ModelSelector is visible at the top (desktop)
  // This logic must match ChatPage to ensure dropdown and top bar are mutually exclusive.
  // - Use explicit RAG mode check (chatModeState.mode !== '3M Knowledge') instead of
  //   isModelSelectorVisible(chatModeState.mode), which incorrectly interprets mode strings as tool names.
  // - Default isWideScreen to false (not true) so that when the prop is absent the top-bar
  //   is assumed absent, and the AI Model entry always falls back to the plus-menu / mobile sheet.
  const showModelSelector = chatModeState.mode !== '3M Knowledge'
    && !isMobile
    && (isWideScreen ?? false)
    && isModelSelectorVisible(chatModeState.selectedTool, isVoiceModeActive);

  const effectiveToolForSend = selectedTool ?? getToolForModelSelection(chatModeState.selectedModelId);

  const { value, setValue, textareaRef, handleSubmit, handleSendClick, handleKeyDown: formKeyDown } = useChatInputForm({
    onNavigateToChat, onHeightChange, externalValue, onExternalValueApplied, isStreaming, selectedTool,
    effectiveToolForSend, uploadedFiles, chatPage, iconSize, isTablet, isMobile,
    isSpeechListening, speechTranscript, firstToolUsed, setFirstToolUsed,
    isCompareActive, setIsCompareActive, isSummarizeActive, setIsSummarizeActive, dispatch,
  });

  // ─── useChatToolSelection hook ────────────────────────────────────────────
  // Break circular dependency: useChatToolSelection needs uploadFilesBatch,
  // but useChatFileUpload needs manuallyClearedToolRef from useChatToolSelection.
  // Solution: use a ref-based wrapper for uploadFilesBatch.
  const uploadFilesBatchRef = React.useRef<(files: { name: string; type: string; data: string }[]) => Promise<void>>(async () => {});
  const fileUploadSetFileWarningRef = React.useRef<React.Dispatch<React.SetStateAction<any>>>(() => {});
  // Stable ref for uploadedFiles — passed to useChatToolSelection so it always reads latest files
  const uploadedFilesRefForToolSelection = React.useRef(uploadedFiles);
  uploadedFilesRefForToolSelection.current = uploadedFiles;

  const {
    showConfirmation,
    setShowConfirmation,
    confirmationConfig,
    setConfirmationConfig,
    handleOptionClick,
    handleRemoveTool,
    proceedWithToolSelection,
    manuallyClearedToolRef,
  } = useChatToolSelection({
    dispatch,
    selectedTool,
    uploadedFiles,
    hasMessages,
    chatModeState,
    onClearChat,
    shouldClearForTargetTool,
    isMobile,
    isTablet,
    chatPage,
    onNavigateToChat,
    textareaRef,
    uploadFilesBatch: (...args) => uploadFilesBatchRef.current(...args),
    uploadedFilesRef: uploadedFilesRefForToolSelection,
    setFileWarning: (v: any) => { fileUploadSetFileWarningRef.current(v); },
    setIsFileUploadDialogOpen,
    setIsFileUploadActive,
    setIsTranslationCollapsed,
    setIsMobileToolsMenuOpen,
  });

  // ─── useChatFileUpload hook ───────────────────────────────────────────────
  const fileUpload = useChatFileUpload({
    dispatch,
    msalInstance: msalInstance,
    uploadedFiles,
    selectedTool,
    sessionId,
    hasMessages,
    onClearChat,
    fileInputRef,
    translationFileInputRef,
    isFileUploadDialogOpen,
    setIsFileUploadDialogOpen,
    isStreaming,
    globalBlocking,
    effectiveToolForSend,
    shouldHideTools,
    isRagEnabled: !!chatModeState.ragChatType,
    setIsFileUploadActive,
    setIsCompareActive,
    setIsTranslationCollapsed,
    manuallyClearedToolRef,
  });

  // Wire up the ref-based uploadFilesBatch now that fileUpload is available
  uploadFilesBatchRef.current = fileUpload.uploadFilesBatch;
  fileUploadSetFileWarningRef.current = fileUpload.setFileWarning;

  // Destructure commonly-used values from useChatFileUpload
  const {
    stagedFiles, setStagedFiles,
    uploadError, setUploadError,
    fileWarning, setFileWarning,
    isDataAnalysisUploading,
    showImmediateSpinner,
    awaitingFileSelection, setAwaitingFileSelection,
    filesBeingProcessedRef,
    isDraggingOver, setIsDraggingOver,
    isFileDragInProgress,
    showBlockingMessage, setShowBlockingMessage,
    hideFileUploadDialogDropzone,
    handleFileChange,
    handlePaste,
    handleRemoveFile,
    handleClearAllFiles,
    getAllowedFileTypes,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleCancelDirectDropUpload,
    handleRemoveStagedFile,
    handleFilesDroppedOnDialog,
    handleFileUploadDialogDropzoneClick,
    handleFileUploadDialogUpload,
    handleFileUploadDialogClose,
    handleFileUploadDialogCancelFileRead,
    translationActiveReadersRef,
  } = fileUpload;

  // Simple file type checkers used in JSX
  const isImageFileType = (fileType: string) => {
    return fileType === 'image/png' || fileType === 'image/jpeg' || fileType === 'image/jpg';
  };
  const isDataFileType = (fileType: string) => {
    return fileType === 'text/csv' || fileType === 'application/vnd.ms-excel' || fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  };
  const isTextDocumentType = (fileType: string) => {
    return fileType === 'application/pdf' || 
           fileType === 'application/msword' || 
           fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
           fileType === 'text/plain' ||
           fileType === 'application/vnd.ms-powerpoint' ||
           fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
           fileType === 'application/vnd.ms-outlook';
  };

  // Auto-focus textarea when streaming ends so user can immediately type next prompt
  const prevIsStreamingRef = React.useRef(isStreaming);
  React.useEffect(() => {
    if (prevIsStreamingRef.current === true && isStreaming === false) {
      // Streaming just ended — focus the textarea (skip on mobile/tablet to avoid keyboard popup)
      if (!isMobile && !isTablet && textareaRef.current) {
        textareaRef.current.focus();
      }
    }
    prevIsStreamingRef.current = isStreaming;
  }, [isStreaming, isMobile, isTablet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-collapse translation mode on mobile when chat has messages
  React.useEffect(() => {
    if (selectedTool === 'translation' && translationHistory.length > 0) {
      setIsTranslationCollapsed(true);
    }
  }, [selectedTool, translationHistory.length]);

  // Toggle body class so Gallery tiles become unclickable while dictating
  React.useEffect(() => {
    document.body.classList.toggle('dictation-active', isSpeechListening);
  }, [isSpeechListening]);

  // Wrap startSpeech so we snapshot the current textarea value before the
  // session begins. preDictationValueRef is used by the live-preview (value
  // prop) and the processing effect so appending works correctly every time.
  const handleStartSpeech = React.useCallback(() => {
    preDictationValueRef.current = value;
    // Ensure dictation service has MSAL for auth
    dictationService.setMsalInstance(msalInstance);
    startSpeech();
  }, [value, startSpeech, msalInstance]);

  // Wrap hook's handleKeyDown to add dictation shortcut (Ctrl+Shift+D)
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    formKeyDown(e);
    // Ctrl+Shift+D — Toggle dictation
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
      e.preventDefault();
      if (isSpeechListening) {
        if (dictationMode === 'batch') {
          confirmDictation();
        } else {
          stopSpeech();
        }
      } else if (!isStreaming && !isSpeechTranscribing && !isSpeechStarting) {
        handleStartSpeech();
      }
    }
  }, [formKeyDown, isSpeechListening, isSpeechTranscribing, isSpeechStarting, dictationMode, confirmDictation, stopSpeech, handleStartSpeech, isStreaming]);

  // Whenever the Whisper transcript updates (new chunk transcribed), commit
  // the full accumulated transcript into the textarea.
  React.useEffect(() => {
    if (speechTranscript && !isSpeechListening) {
      setValue((prev) => (prev ? prev + ' ' + speechTranscript : speechTranscript));
      resetSpeech();
      const scrollTimer = setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
      }, 80);
      return () => clearTimeout(scrollTimer);
    }
  }, [speechTranscript, isSpeechListening, resetSpeech]); // eslint-disable-line react-hooks/exhaustive-deps

  // Surface speech recognition errors through the existing warning toast.
  // For mic permission denial, reuse the same MicrophonePermissionDialog that
  // voice mode shows — it has detailed browser instructions (styled dialog).
  React.useEffect(() => {
    if (!speechError) return;
    if (speechError.toLowerCase().includes('microphone access denied') ||
        speechError.toLowerCase().includes('enable microphone permissions')) {
      setShowMicPermissionDialog(true);
    } else {
      console.warn('[Dictation] Transcription error:', speechError);
      setShowDictationError(true);
    }
  }, [speechError, setShowMicPermissionDialog, setShowDictationError]);

  // ensureSessionId on mount
  useEffect(() => {
    dispatch(ensureSessionId());
  }, [dispatch]);

  // (Drag-and-drop prevention is handled by useChatFileUpload hook)

  // Update tool text width based on window size with debouncing (visual only)
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const updateWidth = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setToolTextWidth(40);
      } else if (width < 640) {
        setToolTextWidth(60);
      } else if (width < 768) {
        setToolTextWidth(80);
      } else if (width < 1024) {
        setToolTextWidth(100);
      } else {
        setToolTextWidth(120);
      }
    };

    const debouncedUpdateWidth = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateWidth, 150);
    };

    updateWidth();
    window.addEventListener('resize', debouncedUpdateWidth);
    return () => {
      window.removeEventListener('resize', debouncedUpdateWidth);
      clearTimeout(timeoutId);
    };
  }, []);

  // Track previous height to avoid unnecessary updates
  const previousHeightRef = React.useRef<number>(0);
  const hasReportedHeightRef = React.useRef<boolean>(false); // Track if we've reported a real height

  // Calculate and report total component height - runs AFTER render
  React.useEffect(() => {
    if (!bottomBubbleContainerRef.current || !onHeightChange) {
      return; // Skip measurement while dropdown is open
    }
    
    // Wait for browser to paint, then measure
    const rafId = requestAnimationFrame(() => {
      if (!bottomBubbleContainerRef.current) return;
      
      const baseHeight = isTablet ? 64 : 56;
      const maxContentHeight = 128;
      
      // Calculate offsets
      const BASE_PAGE_OFFSET = !chatPage ? 110 : 0;
      const GREETING_INPUT_PADDING = !chatPage ? (iconSize || 32) : 0;
      const GREETING_SPACER = !chatPage ? 36 : 0;
      const EXTRA_GALLERY_OFFSET = BASE_PAGE_OFFSET + GREETING_INPUT_PADDING + GREETING_SPACER;
      
      // Measure the actual height of the bottom bubble container (tools + files + dropdown)
      const bottomBubbleHeight = bottomBubbleContainerRef.current.offsetHeight;
      
      // Calculate finalHeight from textarea if available
      let currentFinalHeight = baseHeight;
      if (textareaRef.current) {
        const currentScrollHeight = textareaRef.current.scrollHeight;
        const currentContentHeight = Math.max(currentScrollHeight, baseHeight);
        const currentNeedsScrolling = currentContentHeight > maxContentHeight;
        currentFinalHeight = currentNeedsScrolling ? maxContentHeight : currentContentHeight;
      }
      
      const totalComponentHeight = currentFinalHeight + bottomBubbleHeight + EXTRA_GALLERY_OFFSET;
      
      // Only update if height changed by more than 10px to prevent measurement fluctuations and feedback loops
      if (Math.abs(totalComponentHeight - previousHeightRef.current) > 10) {
        previousHeightRef.current = totalComponentHeight;
        hasReportedHeightRef.current = true; // Mark that we've reported a real height
        onHeightChange(totalComponentHeight);
      }
    });
    
    return () => cancelAnimationFrame(rafId);
  }, [value, selectedTool, uploadedFiles.length, onHeightChange, chatPage, iconSize, isTablet, isFilePreviewCollapsed, translationHistory.length, isTranslationCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Report initial height on mount to ensure padding is correct immediately
  React.useEffect(() => {
    if (!onHeightChange) return;
    
    // Only report initial height if we haven't reported a real height yet
    if (hasReportedHeightRef.current) {
      return;
    }
    
    // Report default height immediately when component mounts
    const baseHeight = isTablet ? 64 : 56;
    const BASE_PAGE_OFFSET = !chatPage ? 110 : 0;
    const GREETING_INPUT_PADDING = !chatPage ? (iconSize || 32) : 0;
    const GREETING_SPACER = !chatPage ? 36 : 0;
    const EXTRA_GALLERY_OFFSET = BASE_PAGE_OFFSET + GREETING_INPUT_PADDING + GREETING_SPACER;
    
    // Initial height = base textarea + minimal tool bar (approximately 60px)
    const initialHeight = baseHeight + 60 + EXTRA_GALLERY_OFFSET;
    previousHeightRef.current = initialHeight;
    onHeightChange(initialHeight);
  }, [onHeightChange, chatPage, iconSize, isTablet]);


  // Initialize selected tool from initialTools prop, but only if not in RAG mode
  useEffect(() => {
    // Only set initial tool if in Ask Chat mode and not just after a Home navigation
    if (chatModeState.mode === 'Ask Chat' && initialTools && initialTools.length > 0 && !selectedTool && !manuallyClearedToolRef.current) {
      dispatch(setSelectedTool(initialTools[0]));
    }
  }, [initialTools, selectedTool, chatModeState.mode, chatModeState.ragChatType, dispatch, manuallyClearedToolRef]);

  // Set firstToolUsed when initialTools is provided and conversation starts (chatPage mode)
  useEffect(() => {
    if (chatPage && initialTools && initialTools.length > 0 && !firstToolUsed) {
      setFirstToolUsed(initialTools[0]);
    }
  }, [chatPage, initialTools, firstToolUsed]);


  // Clear selected tool when switching RAG chat types
  useEffect(() => {
    if (chatModeState.ragChatType) {
      // When switching to a RAG type, clear any selected tool so the placeholder updates
      dispatch(setSelectedTool(null));
    }
  }, [chatModeState.ragChatType, dispatch]);

  // Dropdown toggle removed.

  const chatOptions: ChatOption[] = React.useMemo(() => [
    // Removed standalone File Upload tool – uploads now handled either in general chat (no selected tool) or data_analysis.
    { label: 'Web Search', icon: TbWorldSearch, apiLabel: 'web_search' },
    { label: 'Reasoning Model', icon: ReasoningModelIcon, apiLabel: 'reasoning' },
    { label: 'Create Image', icon: PiImage, apiLabel: 'image_generation' },
    { label: 'Data Analysis', icon: DataAnalysisIcon, apiLabel: 'data_analysis' },
    { label: 'Document Translation', icon: MdTranslate, apiLabel: 'translation' },
  ], []);

  const handleModelSelection = React.useCallback((modelId: string) => {
    dispatch(setSelectedTool(getToolForModelSelection(modelId)));
    dispatch(setSelectedModel(modelId));
  }, [dispatch]);

  React.useEffect(() => {
    if (effectiveToolForSend === 'image_generation' && isFileUploadActive) {
      setIsFileUploadActive(false);
    }
  }, [effectiveToolForSend, isFileUploadActive]);

  // File upload is not available when the active/effective tool is image generation
  const isFileUploadSupported = effectiveToolForSend !== 'image_generation';

  // handleOptionClick, proceedWithToolSelection, handleRemoveTool provided by useChatToolSelection hook
  const getToolDisplayName = (apiLabel: string) => {
    const option = chatOptions.find(opt => opt.apiLabel === apiLabel);
    return option?.label || apiLabel;
  };

  const triggerAdditionalUpload = React.useCallback(() => {
    // Hide any existing global blocking overlay before showing dialog
    dispatch(hideGlobalBlocking());
    // Clear any previously selected tool — file upload from the plus menu is general-chat BYOD,
    // so no tool name should be sent in the payload.
    dispatch(setSelectedTool(null));
    manuallyClearedToolRef.current = true;
    // Track which files exist before opening dialog (so we know which ones to keep/remove)
    setStagedFiles([]);
    // Show the custom file upload dialog instead of directly triggering file picker
    setIsFileUploadDialogOpen(true);
  }, [dispatch, manuallyClearedToolRef, setStagedFiles]);

  // Listen for gallery tool selection events
  useEffect(() => {
    const handleGalleryToolSelection = (event: Event) => {
      if (isSpeechListening) return;
      const customEvent = event as CustomEvent<{ tool: string }>;
      const toolName = customEvent.detail.tool;
      
      logger.log('[ChatInput] Gallery tool selection event received:', toolName);
      
      // Map gallery tool names to API labels
      const toolMapping: { [key: string]: string } = {
        'image_generation': 'image_generation',
        'web_search': 'web_search',
        'reasoning': 'reasoning',
        'data_analysis': 'data_analysis',
        'upload_file': 'upload_file', // For general file upload
        'none': 'none', // Explicitly disable any selected tool
        'translation': 'translation'
      };
      
      const apiLabel = toolMapping[toolName];
      
      logger.log('[ChatInput] Mapped to apiLabel:', apiLabel);
      
      if (apiLabel === 'upload_file') {
        // Trigger file upload dialog for general chat (BYOD) - same as clicking BYOD button
        logger.log('[ChatInput] Triggering file upload dialog');
        triggerAdditionalUpload();
      } else if (apiLabel === 'none') {
        // Disable any currently selected tool (switch to general chat mode)
        logger.log('[ChatInput] Clearing tool selection');
        manuallyClearedToolRef.current = true;
        dispatch(setSelectedTool(null));
        dispatch(clearTargetLanguages());
      } else if (apiLabel && apiLabel !== '') {
        // Directly call proceedWithToolSelection to bypass confirmation dialogs
        // Gallery tiles should act as direct tool selection
        logger.log('[ChatInput] Proceeding with tool selection:', apiLabel);
        proceedWithToolSelection(apiLabel);
      }
    };
    
    window.addEventListener('galleryToolSelection', handleGalleryToolSelection);
    return () => window.removeEventListener('galleryToolSelection', handleGalleryToolSelection);
  }, [dispatch, proceedWithToolSelection, triggerAdditionalUpload, isSpeechListening, manuallyClearedToolRef]);

  const handleTranslateDocuments = async () => {
    if (!uploadedFiles || uploadedFiles.length === 0) {
      logger.error('[translate] No files to translate');
      return;
    }

    if (!targetLanguages || targetLanguages.length === 0) {
      logger.error('[translate] No target languages selected');
      return;
    }

    // Check if this is a duplicate translation (same files + languages)
    const currentFileNames = uploadedFiles.map(f => f.name).sort();
    const currentLanguageCodes = targetLanguages.map(l => l.languageCode).sort();
    const currentGlossaryIDs = (glossaryFiles || []).map(g => `${g.lang}:${g.file.filename}`).sort();
    const user = auth.profile?.account?.username;
    
    // Compare with last translation to detect duplicates or incremental changes
    const filesAreSame = lastTranslation && 
      JSON.stringify(lastTranslation.fileNames) === JSON.stringify(currentFileNames);

    const glossariesAreSame = lastTranslation &&
      JSON.stringify(lastTranslation.glossaryFiles || []) === JSON.stringify(currentGlossaryIDs);
    
    const isDuplicate = filesAreSame && glossariesAreSame &&
      JSON.stringify(lastTranslation.languageCodes) === JSON.stringify(currentLanguageCodes);
    
    if (isDuplicate) {
      logger.warn('[translate] Duplicate translation request - same files, glossaries and languages');
      return;
    }

    // Determine if we should do incremental translation (same files + glossaries, new languages)
    let languagesToTranslate: ILanguageOption[];
    let isIncrementalTranslation = false;
    
    if (filesAreSame && glossariesAreSame && lastTranslation) {
      // Files are the same, check if there are new languages
      const previousLanguageCodes = new Set(lastTranslation.languageCodes);
      const newLanguages = targetLanguages.filter(
        lang => !previousLanguageCodes.has(lang.languageCode)
      );
      
      if (newLanguages.length > 0) {
        // We have new languages to translate
        languagesToTranslate = newLanguages;
        isIncrementalTranslation = true;
        logger.info('[translate] Incremental translation - same files, new languages', {
          previousLanguages: lastTranslation.languageCodes,
          newLanguages: newLanguages.map(l => l.languageCode)
        });
      } else {
        // No new languages, this shouldn't happen due to isDuplicate check above
        logger.warn('[translate] No new languages found');
        return;
      }
    } else {
      // Files changed or no previous translation - translate all languages
      languagesToTranslate = targetLanguages;
      isIncrementalTranslation = false;
    }

    // Update last translation tracking in Redux
    dispatch(setLastTranslation({
      fileNames: currentFileNames,
      languageCodes: currentLanguageCodes,
      glossaryFiles: currentGlossaryIDs
    }));

    try {
      translationCompletedRef.current = false;

      logger.info('[translate] Starting translation', {
        fileCount: uploadedFiles.length,
        languages: languagesToTranslate.map(l => l.languageCode),
        isIncremental: isIncrementalTranslation
      });

      const messageContent = isIncrementalTranslation
          ? `Translate the attached document(s) to additional languages: ${languagesToTranslate.map(l => l.label).join(', ')}`
          : `Translate the attached document(s) to: ${languagesToTranslate.map(l => l.label).join(', ')}`;
        
      const userMessage = {
          id: `translate-user-${Date.now()}`,
          role: 'user',
          content: buildTranslationMessageContent(uploadedFiles, messageContent),
          timestamp: new Date().toISOString(),
      };
      
      dispatch(setIsTranslating(true));

      // Always append to history to keep a log of all translations in the session
      if (translationHistory.length > 0) {
        dispatch(setTranslationHistory([...translationHistory, userMessage]));
      } else {
        dispatch(setTranslationHistory([userMessage])); 
      }

      // Convert uploaded files to File objects
      const files: File[] = await Promise.all(
        uploadedFiles.map(async (uploadedFile) => {
          const mimeType = uploadedFile.type || 'application/octet-stream';
          const storedData = uploadedFile.data;

          // CSV and other text-based files are stored as plain text (readAsText), not base64.
          // Binary files (docx, xlsx, pdf, etc.) are stored as base64 (readAsArrayBuffer).
          const isTextBased = /\.csv$/i.test(uploadedFile.name) || mimeType === 'text/csv' || mimeType === 'text/plain';

          let blob: Blob;
          if (isTextBased) {
            // Plain text — create blob directly from the string
            blob = new Blob([storedData], { type: mimeType });
          } else {
            // Base64-encoded binary — decode to binary first
            const byteCharacters = atob(storedData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
          }

          return new File([blob], uploadedFile.name, { type: mimeType });
        })
      );

      // Create translation service instance
      const translationService = createDocumentTranslationService(msalInstance);
      // Get language codes array (only new languages if incremental)
      const targetLanguageCodes = languagesToTranslate.map(lang => lang.languageCode);

      // Call translation service
      const result = await translationService.translateDocuments(
        files,
        targetLanguageCodes,
        glossaryFiles,
        user,
        sessionId,
        (payload) => dispatch(setTranslationQuota(payload)) // Update quota in Redux as we receive it
      );

      logger.info('[translate] Translation completed', { result });
      
      // Log the full result structure for debugging
      logger.log('[translate] Full response:', JSON.stringify(result, null, 2));
      logger.log('[translate] Response keys:', Object.keys(result));
      logger.log('[translate] Result type:', result.type);
      logger.log('[translate] Result data:', result.data);

      // Extract translated files from nested structure
      // The response can be either:
      // 1. Direct format: { translated_files: [...], error_message: "..." }
      // 2. Nested format: { data: { content: { translated_files: [...] } } }
      // 3. Streaming format: { type: "content", data: { content: { translated_files: [...] } } }
      const translatedFiles = result.translated_files || 
                           result.data?.content?.translated_files ||
                           result.content?.translated_files ||
                           [];
      
      const errorMessage = result.error_message || 
                          result.data?.content?.error_message || 
                          result.content?.error_message ||
                          result.error_data?.message;

      logger.log('[translate] Extracted translated files:', translatedFiles);
      logger.log('[translate] Extracted error message:', errorMessage);

      // Add assistant message to chat with download buttons
      if (translatedFiles && translatedFiles.length > 0) {
        logger.info('[translate] Translation completed successfully', {
          fileCount: translatedFiles.length,
          isIncremental: isIncrementalTranslation
        });
        
        // Prepare translation data for the message
        const translationData = translatedFiles.map((f: any) => ({
          filename: f.filename,
          content_base64: f.content_base64,
          language: f.language || f.language_name
        }));
        
        logger.log('[translate] Prepared translationData:', translationData);
        logger.log('[translate] translationData length:', translationData.length);
        logger.log('[translate] translationData[0]:', translationData[0]);
        
        const successMessage = isIncrementalTranslation
          ? `Additional translations completed successfully! You can now download your file(s):\n\n${translatedFiles.map((f: any) => 
              `**${f.filename}** (${f.language || f.language_name})`
            ).join(', ')}`
          : `Translation completed successfully! You can now download your file(s):\n\n${translatedFiles.map((f: any) => 
              `**${f.filename}** (${f.language || f.language_name})`
            ).join(', ')}`;
        
        const assistantMessage = {
          id: `translate-assistant-${Date.now()}`,
          timestamp: new Date().toISOString(),
          role: 'assistant',
          content: successMessage,
          translationData // Store translation data for download buttons
        };

        // Use addTranslationMessage (appends in the reducer) instead of spreading the
        // stale-closure copy of translationHistory — the async gap between the user-message
        // dispatch and this point means translationHistory captured in this closure is
        // no longer current.
        dispatch(addTranslationMessage(assistantMessage));
        
        logger.log('[translate] Created assistant message with translationData');
        logger.log('[translate] assistantMessage.translationData:', assistantMessage.translationData);
        logger.log('[translate] Full assistantMessage:', JSON.stringify(assistantMessage, null, 2));
        
        // Mark translation as completed to prevent placeholder from being added
        translationCompletedRef.current = true;
      } else {
        // Unknown response format
        logger.warn('[translate] No translated_files in response', { 
          resultKeys: Object.keys(result),
          fullResult: result 
        });
        logger.error('[translate] Unexpected response format - no translated_files or error_message');
      }
    } catch (error) {
      logger.error('[translate] Translation error', { error });
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Add error message to history instead of using transient error state
      // This ensures the error persists in the conversation flow
      const errorMessage = {
        id: `translate-error-${Date.now()}`,
        role: 'assistant',
        content: `${errorMsg}`,
        error: errorMsg,
        timestamp: new Date().toISOString()
      };
      
      dispatch(addTranslationMessage(errorMessage));
      dispatch(setTranslationError('')); // Clear transient error
    } finally {
      dispatch(setIsTranslating(false));
    }
  };

  // Memoize cursor color to prevent recalculation
  const cursorColor = React.useMemo(() => {
    if (accent !== 'none') {
      return 'var(--color-accent)';
    }
    
    // For 'none' accent, force visible cursor colors based on theme mode
    if (typeof window !== 'undefined') {
      const htmlElement = document.documentElement;
      const theme = htmlElement.getAttribute('data-theme') || '';
      
      // Check if it's a dark theme
      if (theme.startsWith('dark-')) {
        return '#9ca3af'; // Much lighter gray for dark themes (gray-400)
      } else {
        return '#374151'; // Dark gray for light themes (gray-700)
      }
    }
    
    // Fallback - use a neutral visible color
    return '#6b7280'; // gray-500 as fallback
  }, [accent]);

  // Memoize textarea styles to prevent object recreation
  const textareaStyles = React.useMemo(() => ({
    scrollbarWidth: 'thin' as const,
    scrollbarColor: '#9CA3AF transparent',
    WebkitScrollbarWidth: '6px',
    fontSize: 'var(--app-font-size)', // Respects user font size preference from theme settings
    lineHeight: '24px',
    paddingTop: (isMobile) ? '8px' : isTablet ? '10px' : '12px',
    paddingBottom: '12px', // Always use base padding - no extra space for tool
    paddingLeft: '16px', // Reduced left padding (removed plus button)
  // Increase right padding so user text never flows beneath the right-side icon cluster.
  // After redesign: only 2 icons on the right (dictate + voice-agent/send).
  // Each icon button ≈ 40px.
  //   Ask Chat, Cancel loading          : Cancel(~156px) + mic(~40px)                → 200px
  //   Ask Chat, speech supported        : mic(~40px) + voice/send(~40px)             → 100px
  //   Ask Chat, no STT / voice active   : voice/send(~40px)                          →  60px
  //   3M Knowledge, speech supported    : mic(~40px) + send(~40px)                   → 100px
  //   3M Knowledge, no STT              : send(~40px)                                →  60px
    paddingRight: voiceModeLoading
      ? '200px'                                                    // Cancel + mic
      : !isVoiceModeActive
        ? '100px'                                                  // mic + voice/send
        : '60px',                                                  // voice/send only
    caretColor: cursorColor,
    verticalAlign: 'top' as const,
    boxShadow: 'none', // Always remove shadow to eliminate seam above bubbles
    transition: 'border-color 0.2s ease-in-out, outline 0.2s ease-in-out, padding-right 0.2s ease-in-out',
  } as React.CSSProperties & {
    WebkitScrollbarWidth?: string;
  }), [cursorColor, isMobile, isTablet, voiceModeLoading, isVoiceModeActive]);

  // Get active function call from last assistant message (for voice mode spinner)
  const activeFunctionCall = React.useMemo(() => {
    const messages = chatModeState.mode === 'Ask Chat' 
      ? chatModeState.askChatHistory 
      : chatModeState.ragHistory;
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'assistant' && lastMessage.functionCall?.isActive) {
      return lastMessage.functionCall;
    }
    return null;
  }, [chatModeState.askChatHistory, chatModeState.mode, chatModeState.ragHistory]);

  return (
    <>
      {/* Voice Mode Circle - shown immediately when voice mode is active AND on main content page */}
      {isVoiceModeActive && chatPage && createPortal(
        <VoiceCircle
          isListening={isVoiceModeListening}
          isPaused={isVoiceModePaused}
          audioLevel={audioLevel}
          frequencyData={frequencyData}
          isRestarting={isVoiceModeRestarting}
          showInactivityWarning={showInactivityWarning}
          hasFunctionCallInProgress={activeFunctionCall !== null}
          onPauseToggle={handleVoiceModePauseToggle}
          onStop={handleVoiceModeStop}
          isDarkMode={mode === 'dark'}
        />,
        document.body
      )}
      
      {/* Chat Input - hidden when voice mode is active on main content page, instant switch */}
      {!(isVoiceModeActive && chatPage) && (
        <motion.form 
          // No padding on ChatInput itself - parent containers handle alignment
          className={`w-full mx-auto flex flex-col justify-center ${!chatPage ? 'sm:pt-0' : ''}`}
          style={{ maxWidth: '2000px' }}
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
      {/* Drag and drop overlay - wraps only the input container */}
      <div 
        className={`relative ${isDraggingOver ? 'dragging-active' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDraggingOver && (
          <div 
            className={`absolute inset-0 z-[9999] flex items-center justify-center ${showImmediateSpinner ? 'pointer-events-auto' : 'pointer-events-none'}`}
            onMouseDown={showImmediateSpinner ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCancelDirectDropUpload();
            } : undefined}
            style={{
              backgroundColor: 'rgba(94, 149, 237, 0.1)',
              border: '3px dashed #5E95ED',
              borderRadius: '8px'
            }}
          >
            {showImmediateSpinner ? (
              <div className="flex flex-col items-center gap-2 p-4 pt-2 pb-2 rounded-lg bg-[var(--color-secondary)] shadow-xl max-w-sm">
                <div className="flex items-center gap-2">
                  <div 
                    style={{
                      width: 42,
                      height: 42,
                      border: '5px solid rgba(94, 149, 237, 0.25)',
                      borderTop: '5px solid #5E95ED',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  <p className="text-base font-semibold text-[#5E95ED]">Waiting for file<span className="animate-pulse">...</span></p>
                </div>
                <p className="text-xs text-[var(--color-text)] opacity-70 text-center">Click to cancel</p>
              </div>
            ) : (
              // Show drop message when hovering
              <div className="flex flex-col items-center gap-2 p-4 pt-2 pb-2 rounded-lg bg-[var(--color-secondary)] shadow-xl max-w-sm" style={{ pointerEvents: 'none' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#5E95ED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p className="text-base font-semibold text-[#5E95ED]">Drop files here to upload</p>
                <p className="text-xs text-[var(--color-text)] opacity-70 text-center">
                  {selectedTool === 'data_analysis' 
                    ? 'CSV or Excel files only (max 2 files, 16MB each)'
                    : 'Images, documents, or data files'}
                </p>
              </div>
            )}
          </div>
        )}

      {/* Centered error modal when uploadError active */}
      {uploadError && selectedTool && (selectedTool === 'file_upload' || selectedTool === 'data_analysis') && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          style={{ zIndex: 20000 }}
          aria-live="assertive"
          onMouseDown={(e) => {
            // Clicking backdrop closes dialog
            if (e.target === e.currentTarget) {
              logger.log('[upload-error-modal] Backdrop clicked');
              setUploadError(null);
              setAwaitingFileSelection(false);
              filesBeingProcessedRef.current = false;
              manuallyClearedToolRef.current = true;
              dispatch(setSelectedTool(null));
              dispatch(clearUploadedFiles());
              dispatch(clearTargetLanguages());
              dispatch(hideGlobalBlocking());
            }
          }}
        >
          <div
            className="flex items-start gap-3 p-4 rounded-md border border-red-600 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 shadow-xl max-w-md w-full mx-4"
            onMouseDown={(e) => { e.stopPropagation(); }}
          >
          <div className="flex-shrink-0" aria-hidden="true">
            {/* Simple warning icon using radial exclamation */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l9 19H3L12 2z" />
              <line x1="12" y1="10" x2="12" y2="14" />
              <circle cx="12" cy="18" r="1" />
            </svg>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-sm">Could not upload file at this time.</span>
            <span className="text-xs">{uploadError.file}: {uploadError.message}</span>
            <div className="flex gap-2 mt-1 flex-wrap">
              <button
                type="button"
                className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadError(null);
                  setAwaitingFileSelection(false);
                  filesBeingProcessedRef.current = false;
                  if (fileInputRef.current) {
                    fileInputRef.current.value='';
                    setTimeout(()=>{
                      fileInputRef.current?.click();
                    },0);
                  }
                }}
              >Select File Again</button>
              <button
                type="button"
                className="px-3 py-1 text-xs rounded bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-400 dark:hover:bg-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadError(null);
                  setAwaitingFileSelection(false);
                  filesBeingProcessedRef.current = false;
                  manuallyClearedToolRef.current = true;
                  dispatch(setSelectedTool(null));
                  dispatch(clearUploadedFiles());
                  dispatch(clearTargetLanguages());
                  dispatch(hideGlobalBlocking());
                }}
              >Close</button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}
        <Label.Root htmlFor="chat-input" className="sr-only">Chat Input</Label.Root>
        <div className="relative w-full group flex flex-col">
        {selectedTool !== 'translation' && (
          <div className="relative">
            {/* Batch dictation mode: show waveform instead of textarea while recording/processing/booting */}
            {dictationMode === 'batch' && (isSpeechStarting || isSpeechListening || isSpeechTranscribing) ? (
              <div
                className={`w-full ${isTablet ? 'min-h-[64px]' : 'min-h-[56px]'} bg-[var(--color-secondary)] rounded-t-lg 
                border-l border-r border-t border-[var(--color-accent)] group-focus-within:!border-l-[#5E95ED] group-focus-within:!border-r-[#5E95ED] group-focus-within:!border-t-[#5E95ED]`}
                style={{ display: 'flex', alignItems: 'center', padding: '12px 0', position: 'relative' }}
              >
                <DictationWaveform
                  audioLevel={speechAudioLevel}
                  frequencyData={speechFrequencyData}
                  isProcessing={isSpeechTranscribing}
                  isBooting={isSpeechStarting}
                />
              </div>
            ) : (
            <textarea
              ref={textareaRef}
              id="chat-input"
              className={`w-full ${isTablet ? 'min-h-[64px]' : 'min-h-[56px]'} max-h-${iconSize || 32} bg-[var(--color-secondary)] text-[var(--color-text)] focus:outline-none resize-none rounded-t-lg 
              border-l border-r border-t border-[var(--color-accent)] group-focus-within:!border-l-[#5E95ED] group-focus-within:!border-r-[#5E95ED] group-focus-within:!border-t-[#5E95ED]`}
              aria-label="Chat message input"
              aria-describedby={selectedTool ? "selected-tool" : undefined}
              // placeholder styling in index.css
              // While dictating, render live transcript in muted/placeholder colour so it
              // looks grayed-out (like the old placeholder) until the user stops.
              style={{
                ...textareaStyles,
                transition: 'color 0.3s ease, border-color 0.2s ease-in-out',
                color: (isSpeechListening && speechTranscript)
                  ? '#646971'                       // same gray used for scrollbarColor / placeholder
                  : undefined,                      // inherit var(--color-text) from textareaStyles
                cursor: isSpeechListening ? 'default' : undefined, // hide text cursor while dictating
              }}
              // Block all keyboard input while dictation is active.
              // readOnly only prevents user edits — the live-preview value prop is a
              // controlled React value so React can still update it freely each onresult.
              readOnly={isSpeechListening || isSpeechTranscribing}
              // 'Listening…' only shown when transcript is empty (value handles the preview)
              placeholder={isSpeechListening ? 'Listening…' : isSpeechTranscribing ? 'Transcribing…' : getPlaceholder()}
              // During listening in ghost mode: render preDictationValue + live transcript so the
              // preview is always visible even when the textarea already has content.
              // In batch mode, textarea is hidden during listening (waveform shown instead).
              value={
                dictationMode === 'ghost' && isSpeechListening && speechTranscript
                  ? (preDictationValueRef.current
                      ? preDictationValueRef.current + ' ' + speechTranscript
                      : speechTranscript)
                  : value
              }
              // No-op during listening: keeps `value` state equal to preDictationValue
              // so the processing effect appends correctly without double-committing.
              onChange={e => { if (!isSpeechListening) setValue(e.target.value); }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows={1}
            />
            )}
          </div>
        )}
        
        {/* Unified tool & file bubble container - always visible */}
          <div 
            ref={bottomBubbleContainerRef}
            id="selected-tool"
            className={`border-l border-r border-b ${selectedTool === 'translation' ? 'border-t rounded-t-lg' : ''} border-[var(--color-accent)] bg-[var(--color-secondary)] rounded-b-lg px-3 py-2 group-focus-within:!border-l-[#5E95ED] group-focus-within:!border-r-[#5E95ED] group-focus-within:!border-b-[#5E95ED] ${selectedTool === 'translation' ? 'group-focus-within:!border-t-[#5E95ED]' : ''}`}
            style={{ boxShadow: 'none', transition: 'border-color 0.2s ease-in-out', scrollbarWidth: 'thin', scrollbarColor: 'var(--color-accent) transparent' }}
            role="group"
            aria-label="Tools and uploaded files"
          >
            {/* Translation Mode Inline UI - appears at top when translation mode is active */}
            {selectedTool === 'translation' && !isTranslationCollapsed && (
              <TranslationMode
                uploadedFiles={uploadedFiles}
                awaitingFileSelection={awaitingFileSelection}
                chatPage={chatPage}
                iconSize={iconSize}
                translationFileInputRef={translationFileInputRef}
                bottomBubbleContainerRef={bottomBubbleContainerRef}
                textareaRef={textareaRef}
                previousHeightRef={previousHeightRef}
                filesBeingProcessedRef={filesBeingProcessedRef}
                onHeightChange={onHeightChange}
                setAwaitingFileSelection={setAwaitingFileSelection}
                setSelectionWarning={(msg) => setFileWarning(msg ? { message: msg, mode: 'general' } : null)}
                setTranslationWarning={(warning) => setFileWarning(warning ? { ...warning, mode: 'translation' } : null)}
                handleRemoveFile={handleRemoveFile}
                handleTranslateDocuments={handleTranslateDocuments}
                handleFileChange={handleFileChange}
                clearChatInputDragOverlay={() => setIsDraggingOver(false)}
                abortTranslationFileReads={() => {
                  abortAndClearReaders(translationActiveReadersRef);
                }}
              />
            )}
            
            {/* Only show tools in Ask Chat mode */}
            {!shouldHideTools && (
              <>
                {/* Mobile: Plus icon button and selected tool display */}
                {isMobile ? (
                  <div className="flex flex-nowrap justify-start gap-2 items-center mb-1">
                    {/* Plus icon button to open tools menu */}
                      <button
                      type="button"
                      onClick={() => setIsMobileToolsMenuOpen(!isMobileToolsMenuOpen)}
                      style={{ color: 'var(--color-text)' }}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[var(--color-accent)] transition-all duration-200 flex-shrink-0"
                      aria-label="Open tools menu"
                      disabled={isFileDragInProgress}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>

                    {/* Display selected tool if any */}
                    {selectedTool && (() => {
                      const selectedOption = chatOptions.find(opt => opt.apiLabel === selectedTool);
                      if (!selectedOption) return null;
                      const IconComponent = selectedOption.icon as React.ComponentType<any>;
                      return (
                        <div className="flex items-center gap-1">
                          <div
                            className="inline-flex items-center gap-2 px-3 py-1.5 h-8 rounded-lg text-xs font-medium border border-[#5E95ED] bg-[var(--color-accent)] text-white"
                          >
                            <span className="flex items-center justify-center">
                              <IconComponent width={iconSize} />
                            </span>
                            <span className="whitespace-nowrap">{selectedOption.label}</span>
                            <span
                              className="flex items-center justify-center cursor-pointer hover:opacity-70"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveTool();
                              }}
                            >
                              <Cross2Icon width={12} height={12} />
                            </span>
                          </div>
                          {/* Collapse/Expand icon for translation tool when active */}
                          {selectedTool === 'translation' && translationHistory.length > 0 && (
                            <div
                              className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#5E95ED] text-[#5E95ED] hover:text-white transition-all duration-200 flex-shrink-0"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsTranslationCollapsed(!isTranslationCollapsed);
                              }}
                              aria-label={isTranslationCollapsed ? 'Expand translation details' : 'Collapse translation details'}
                            >
                              {isTranslationCollapsed ? (
                                <ChevronUpIcon width={iconSize} />
                              ) : (
                                <ChevronDownIcon width={iconSize} />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                  </div>
                ) : (
                  /* Desktop/Tablet: Primary tools inline + secondary tools under + button */
                  <div className="flex items-center gap-2 mb-1">
                    {/* Plus button — opens secondary tools dropdown — outside overflow container so dropdown isn't clipped */}
                    <div className="relative flex-shrink-0">
                      <button
                        ref={desktopPlusButtonRef}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          if (!isDesktopPlusMenuOpen && desktopPlusButtonRef.current) {
                            const rect = desktopPlusButtonRef.current.getBoundingClientRect();
                            const dropdownHeight = 140; // approx height of 3-item menu
                            const spaceBelow = window.innerHeight - rect.bottom;
                            if (spaceBelow < dropdownHeight + 8) {
                              setDesktopPlusMenuPos({ bottom: window.innerHeight - rect.top + 6, left: rect.left });
                            } else {
                              setDesktopPlusMenuPos({ top: rect.bottom + 6, left: rect.left });
                            }
                          }
                          setIsDesktopPlusMenuOpen(prev => !prev);
                        }}
                        data-tutorial="plus-menu-button"
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-all duration-200 ${isDesktopPlusMenuOpen ? 'bg-[#5E95ED] border-[#5E95ED] text-white' : 'border-[var(--color-accent)] text-[var(--color-text)] hover:bg-[#5E95ED] hover:border-[#5E95ED] hover:text-white'} ${(isFileDragInProgress || isSpeechListening) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        aria-label="More tools"
                        disabled={isFileDragInProgress || isSpeechListening}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"/>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                      </button>
                      {/* Secondary tools dropdown — rendered via portal so it's never clipped */}
                      {isDesktopPlusMenuOpen && desktopPlusMenuPos && createPortal(
                        <>
                          <style>{`
                            @keyframes dropdownSlideDown {
                              from { opacity: 0; transform: translateY(-6px) scale(0.97); }
                              to   { opacity: 1; transform: translateY(0)   scale(1);    }
                            }
                            @keyframes dropdownSlideUp {
                              from { opacity: 0; transform: translateY(6px) scale(0.97); }
                              to   { opacity: 1; transform: translateY(0)   scale(1);    }
                            }
                          `}</style>
                          {/* Click-outside overlay — suppressed while tutorial highlights a plus-menu item */}
                          {!isTutorialOnPlusMenuStep && (
                            <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={() => { setIsDesktopPlusMenuOpen(false); setIsDesktopAiModelSubPanelOpen(false); }} />
                          )}
                          <div
                            className="fixed bg-[var(--color-secondary)] border border-[var(--color-accent)] rounded-xl shadow-2xl overflow-hidden min-w-[210px]"
                            style={{
                              ...(desktopPlusMenuPos.top !== undefined ? { top: desktopPlusMenuPos.top } : { bottom: desktopPlusMenuPos.bottom }),
                              left: desktopPlusMenuPos.left,
                              zIndex: 9999,
                              animation: desktopPlusMenuPos.top !== undefined ? 'dropdownSlideDown 0.18s ease-out forwards' : 'dropdownSlideUp 0.18s ease-out forwards',
                            }}
                          >
                            {/* File Upload */}
                            {isFileUploadSupported && (
                            <button
                              type="button"
                              data-tutorial="byod-button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsDesktopPlusMenuOpen(false); setIsFileUploadActive(true); triggerAdditionalUpload(); }}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-[var(--color-text)] transition-colors"
                            >
                              <FileUploadIcon size={18} className="flex-shrink-0" />
                              <span className="text-sm top-heavy">File Upload</span>
                            </button>
                            )}
                            {isFileUploadSupported && <div className="border-t" style={{ borderColor: 'var(--color-border)' }} />}
                            {/* Web Search — separate category between File Upload and secondary tools */}
                            {chatOptions.filter(t => t.apiLabel === 'web_search').map((toolOption) => {
                              const IconComponent = toolOption.icon as React.ComponentType<any>;
                              return (
                                <button
                                  key={toolOption.apiLabel}
                                  type="button"
                                  data-tutorial="web-search-tool"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsDesktopPlusMenuOpen(false); setIsDesktopAiModelSubPanelOpen(false); handleOptionClick(toolOption.apiLabel, e as any); }}
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-[var(--color-text)] transition-colors"
                                >
                                  <IconComponent size={18} className="flex-shrink-0" />
                                  <span className="text-sm top-heavy">{toolOption.label}</span>
                                </button>
                              );
                            })}
                            <div className="border-t" style={{ borderColor: 'var(--color-border)' }} />
                            {/* Secondary tool options — sourced from chatOptions to stay in sync with mobile menu */}
                            {chatOptions.filter(t => ['data_analysis', 'translation'].includes(t.apiLabel)).map((toolOption) => {
                              const IconComponent = toolOption.icon as React.ComponentType<any>;
                              return (
                                <button
                                  key={toolOption.apiLabel}
                                  type="button"
                                  data-tutorial={toolOption.apiLabel === 'data_analysis' ? 'data-analysis-tool' : 'document-translation-tool'}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsDesktopPlusMenuOpen(false); setIsDesktopAiModelSubPanelOpen(false); handleOptionClick(toolOption.apiLabel, e as any); }}
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-[var(--color-text)] transition-colors"
                                >
                                  <IconComponent size={18} className="flex-shrink-0" />
                                  <span className="text-sm top-heavy">{toolOption.label}</span>
                                </button>
                              );
                            })}
                            {/* AI Model: Only show in dropdown if ModelSelector is NOT visible at the top */}
                            {isModelSelectorVisible(selectedTool) && !showModelSelector && (
                              <>
                                <button
                                  ref={desktopAiModelButtonRef}
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault(); e.stopPropagation();
                                    if (desktopAiModelButtonRef.current) {
                                      const rect = desktopAiModelButtonRef.current.getBoundingClientRect();
                                      const estimatedPanelHeight = Math.min((getVisibleModelOptions(selectedTool).length * 52) + 12, window.innerHeight - 16);
                                      const clampedTop = Math.max(8, Math.min(rect.top, window.innerHeight - estimatedPanelHeight - 8));
                                      setDesktopAiModelSubPanelPos({ top: clampedTop, left: rect.right + 6 });
                                    }
                                    setIsDesktopAiModelSubPanelOpen(prev => !prev);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-[var(--color-text)] transition-colors"
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                                    <rect x="2" y="3" width="20" height="14" rx="2" />
                                    <line x1="8" y1="21" x2="16" y2="21" />
                                    <line x1="12" y1="17" x2="12" y2="21" />
                                  </svg>
                                  <span className="text-sm flex-1 text-left top-heavy">AI Model</span>
                                  <ChevronRightIcon width={16} height={16} style={{ opacity: 0.6 }} />
                                </button>
                              </>
                            )}
                          </div>
                          {/* AI Model sub-panel */}
                          {isDesktopAiModelSubPanelOpen && desktopAiModelSubPanelPos && (
                            <div
                              className="fixed bg-[var(--color-secondary)] border border-[var(--color-accent)] rounded-xl shadow-2xl overflow-hidden min-w-[180px]"
                              style={{
                                top: desktopAiModelSubPanelPos.top,
                                left: desktopAiModelSubPanelPos.left,
                                zIndex: 10000,
                                animation: 'dropdownSlideDown 0.18s ease-out forwards',
                                maxHeight: 'calc(100vh - 16px)',
                                overflowY: 'auto',
                              }}
                            >
                              {getVisibleModelOptions(selectedTool).map((model: ModelConfig) => {
                                const isSelected = model.id === chatModeState.selectedModelId;
                                return (
                                  <button
                                    key={model.id}
                                    type="button"
                                    onClick={() => {
                                      handleModelSelection(model.id);
                                      setIsDesktopAiModelSubPanelOpen(false);
                                      setIsDesktopPlusMenuOpen(false);
                                    }}
                                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--color-accent)] hover:text-white text-sm transition-colors"
                                    style={{ color: isSelected ? '#5E95ED' : 'var(--color-text)' }}
                                  >
                                    <span>{model.displayName}</span>
                                    {isSelected && (
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E95ED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </>,
                        document.body
                      )}
                    </div>

                    {/* File Upload pill — shown immediately when selected from '+', count appears after files uploaded */}
                    {(() => {
                      const isFileUploadPillVisible = isFileUploadActive && effectiveToolForSend !== 'image_generation' && selectedTool !== 'data_analysis' && selectedTool !== 'translation';
                      return (
                        <div
                          style={{
                            maxWidth: isFileUploadPillVisible ? '200px' : '0px',
                            overflow: 'hidden',
                            flexShrink: 0,
                            transition: 'max-width 300ms ease-in-out',
                          }}
                        >
                          <button
                            type="button"
                            aria-label={uploadedFiles.length > 0 ? `File Upload (${uploadedFiles.length}) — click to remove` : 'File Upload — click to remove'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (uploadedFiles.length > 0) {
                                handleClearAllFiles();
                              }
                              setIsFileUploadActive(false);
                            }}
                            className="flex-shrink-0 border-none cursor-pointer flex items-center gap-2 h-8 p-2 rounded-lg bg-[var(--color-accent)] text-white text-sm font-medium leading-none whitespace-nowrap"
                          >
                            <FileUploadIcon width={16} height={16} style={{ flexShrink: 0 }} />
                            <span style={{ transform: 'translateY(0.1rem)' }}>{uploadedFiles.length > 0 ? `File Upload (${uploadedFiles.length})` : 'File Upload'}</span>
                            <Cross2Icon width={14} height={14} style={{ display: 'block', flexShrink: 0 }} />
                          </button>
                        </div>
                      );
                    })()}

                    {/* Divider only visible when a secondary tool chip is active */}
                    {(selectedTool === 'data_analysis' || selectedTool === 'translation' || selectedTool === 'web_search') && (
                      <div style={{ height: '28px', width: '1px', background: 'var(--color-text)', margin: '0 8px' }} />
                    )}

                    {/* Primary tools + active secondary chip in scrollable row */}
                    <div className="flex flex-nowrap gap-2 items-center overflow-x-auto scrollbar-hide flex-1" aria-label="Available tools">

                    {/* Secondary tool chip + divider — wrapped so they occupy exactly ONE gap slot when active, zero space when not */}
                    {(() => {
                      const isAnySecondaryActive = selectedTool === 'data_analysis' || selectedTool === 'translation' || selectedTool === 'web_search';
                      return (
                        <div style={{ maxWidth: isAnySecondaryActive ? '350px' : '0px', marginRight: isAnySecondaryActive ? '0px' : '-8px', overflow: 'hidden', display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0, transition: 'max-width 300ms ease-in-out, margin-right 300ms ease-in-out' }}>
                          {chatOptions.filter(t => ['web_search', 'data_analysis', 'translation'].includes(t.apiLabel)).map(toolOption => {
                            const IconComponent = toolOption.icon as React.ComponentType<any>;
                            const isActive = selectedTool === toolOption.apiLabel;
                            const getTutorialAttr = (apiLabel: string) => {
                              if (apiLabel === 'web_search') return 'web-search-tool';
                              if (apiLabel === 'data_analysis') return 'data-analysis-tool';
                              return 'document-translation-tool';
                            };
                            return (
                              <button
                                key={toolOption.apiLabel}
                                type="button"
                                aria-pressed={isActive}
                                aria-hidden={!isActive}
                                tabIndex={isActive ? 0 : -1}
                                aria-label={isActive ? `${toolOption.label} selected` : toolOption.label}
                                data-tutorial={getTutorialAttr(toolOption.apiLabel)}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (isActive && !isSpeechListening) { handleRemoveTool(); } }}
                                className={`tool-button group relative inline-flex items-center gap-0 py-1 h-8 rounded-lg text-xs font-medium border overflow-hidden focus:outline-none flex-shrink-0 tool-button-active tool-button-desktop ${isSpeechListening ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                style={{ lineHeight: '1', maxWidth: isActive ? '200px' : '0px', paddingLeft: isActive ? '8px' : '0px', paddingRight: isActive ? '4px' : '0px', borderWidth: isActive ? '1px' : '0px', transition: 'max-width 300ms ease-in-out, padding-left 300ms ease-in-out, padding-right 300ms ease-in-out, border-width 300ms ease-in-out' }}
                              >
                                <span className="flex items-center justify-center" style={{ minWidth: '18px', height: '18px', lineHeight: '1' }}>
                                  <IconComponent width={18} height={18} size={18} style={{ display: 'block' }} />
                                </span>
                                <span className="whitespace-nowrap overflow-hidden top-heavy" style={{ paddingLeft: '6px', height: '18px', lineHeight: '18px', display: 'inline-block', position: 'relative', top: '0.075rem' }}>
                                  {toolOption.label}
                                </span>
                                <span
                                  className="flex items-center justify-center cursor-pointer"
                                  style={{ height: '18px', lineHeight: '1', paddingLeft: '12px', paddingRight: '4px', paddingTop: '0.05rem' }}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveTool(); }}
                                >
                                  <Cross2Icon width={14} height={14} style={{ display: 'block', flexShrink: 0 }} />
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}



                    {selectedTool === 'translation' && translationHistory.length > 0 && (
                      <div
                        className="ml-auto cursor-pointer inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#5E95ED] text-[#5E95ED] hover:text-white transition-all duration-200 flex-shrink-0"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsTranslationCollapsed(!isTranslationCollapsed); }}
                        aria-label={isTranslationCollapsed ? 'Expand translation details' : 'Collapse translation details'}
                      >
                        {isTranslationCollapsed ? <ChevronUpIcon width={iconSize} /> : <ChevronDownIcon width={iconSize} />}
                      </div>
                    )}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Data Analysis upload spinner - show when uploading files for Data Analysis tool */}
            {isDataAnalysisUploading && (
              <div className="flex items-center gap-2 mb-2" style={{ color: '#5E95ED' }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    border: '4px solid rgba(94, 149, 237, 0.25)',
                    borderTop: '4px solid #5E95ED',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                  aria-label="Uploading file..."
                />
                <span className="text-sm font-medium">Uploading file... Please wait</span>
              </div>
            )}

            {/* Collapsible file preview section - only when files are uploaded AND NOT in translation mode */}
            {uploadedFiles.length > 0 && selectedTool !== 'translation' && (
              <div className="flex flex-col gap-2">
                {/* Collapse/Expand header - only show on chatPage */}
                {chatPage && (
                  <button
                    type="button"
                    onClick={() => setIsFilePreviewCollapsed(!isFilePreviewCollapsed)}
                    className="flex items-center gap-1 text-xs text-[var(--color-text)] opacity-70 hover:opacity-100 transition-opacity self-start"
                    aria-label={isFilePreviewCollapsed ? 'Expand file previews' : 'Collapse file previews'}
                    aria-expanded={!isFilePreviewCollapsed}
                  >
                    {isFilePreviewCollapsed ? (
                      <>
                        <ChevronDownIcon width={16} height={16} className="flex-shrink-0" />
                        <span className="translate-y-[2px]">{uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} attached</span>
                      </>
                    ) : (
                      <>
                        <ChevronUpIcon width={16} height={16} className="flex-shrink-0" />
                        <span className="translate-y-[2px]">Hide files</span>
                      </>
                    )}
                  </button>
                )}
                {/* File preview content */}
                {!isFilePreviewCollapsed && (
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Uploaded file representations: image thumbnails or generic file bubbles */}
                    {uploadedFiles.map(f => {
                      const isImage = /image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name);
                if (isImage) {
                  return (
                    <PreviewableFileAttachment
                      key={f.key || f.name}
                      fileName={f.name}
                      fileType={f.type}
                      fileData={f.data}
                      dataSource="uploaded"
                    >
                      {({ openPreview }) => {
                        const mime = f.type.includes('jpeg') ? 'image/jpeg' : 'image/png';
                        const dataUrl = `data:${mime};base64,${f.data}`;
                        return (
                          <div
                            className="relative w-12 max-w-16 h-12 rounded-md overflow-hidden border border-[#5E95ED] bg-[#202024] dark:bg-[#202024] light:bg-[#E8F3FF] flex items-center justify-center shadow-sm cursor-pointer group"
                            style={{
                              backgroundColor: mode === 'dark' ? '#202024' : '#E8F3FF'
                            }}
                            aria-label={`Image file ${f.name}`}
                            onClick={openPreview}
                          >
                            <img
                              src={dataUrl}
                              alt={f.name}
                              className="object-cover w-full h-full group-hover:opacity-90"
                              style={{ imageRendering: 'auto' }}
                            />
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleRemoveFile(f.name); }}
                              aria-label={`Remove image ${f.name}`}
                              className="absolute top-0 right-0 m-0.5 bg-black/40 hover:bg-black/60 text-white rounded-full p-[2px] leading-none flex items-center justify-center"
                              style={{ lineHeight: 0 }}
                            >
                              <Cross2Icon width={12} height={12} />
                            </button>
                          </div>
                        );
                      }}
                    </PreviewableFileAttachment>
                  );
                }
                return (
                  <PreviewableFileAttachment
                    key={f.key || f.name}
                    fileName={f.name}
                    fileType={f.type}
                    fileData={f.data}
                    dataSource="uploaded"
                  >
                    {({ canPreview, openPreview }) => (
                      <div 
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border border-[#5E95ED] text-[#5E95ED] shadow-sm"
                        style={{
                          backgroundColor: mode === 'dark' ? '#202024' : '#E8F3FF',
                          cursor: canPreview ? 'pointer' : 'default'
                        }}
                        aria-label={`File ${f.name}`}
                        onClick={canPreview ? openPreview : undefined}
                        ref={(el) => {
                          if (el) {
                            const span = el.querySelector('span.truncate');
                            if (span && span.scrollWidth > span.clientWidth) {
                              el.setAttribute('title', f.name);
                            } else {
                              el.removeAttribute('title');
                            }
                          }
                        }}
                      >
                        <span className="flex items-center justify-center flex-shrink-0" style={{ height: '14px' }}>
                          <FileIcon width={iconSize || 14} height={iconSize || 14} />
                        </span>
                        <span 
                          className="truncate leading-none" 
                          style={{ maxWidth: `${toolTextWidth}px`, paddingTop: '1px' }}
                        >
                          {f.name}
                        </span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveFile(f.name); }} className="p-0.5 rounded-full transition-colors flex-shrink-0 hover:bg-[#5E95ED] hover:text-white flex items-center justify-center" aria-label={`Remove uploaded file ${f.name}`}>
                          <Cross2Icon width={12} height={12} />
                        </button>
                      </div>
                    )}
                  </PreviewableFileAttachment>
                );
              })}
              {/* Action buttons for uploaded files */}
              {uploadedFiles.length >= 1 && (
                <button
                  type="button"
                  disabled={isStreaming}
                  className="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded whitespace-nowrap min-w-fit leading-none transition-all"
                  style={{
                    paddingTop: '5px',
                    paddingBottom: '5px',
                    backgroundColor: isSummarizeActive ? '#5E95ED' : 'transparent',
                    color: isSummarizeActive ? 'white' : '#5E95ED',
                    border: '2px solid #5E95ED',
                    opacity: isSummarizeActive ? 1 : 0.9,
                    fontWeight: isSummarizeActive ? '600' : '500',
                  }}
                  onClick={() => {
                    if (isSummarizeActive) {
                      setIsSummarizeActive(false);
                      setValue('');
                    } else {
                      setIsSummarizeActive(true);
                      setIsCompareActive(false);
                      if (isImageFileType(uploadedFiles[0].type)) {
                        setValue('Describe the image by providing a detailed analysis and highlighting the most interesting aspects without making assumptions. Give the result in Markdown format. Return the result in a summary and each component separately, delivering a structured output of its content.');
                      } else if (isDataFileType(uploadedFiles[0].type)) {
                        setValue('Analyze the data in the file.');
                      } else {
                        setValue('Summarize the content.');
                      }
                      if (onExternalValueApplied) onExternalValueApplied();
                    }
                  }}
                  aria-label={isSummarizeActive ? "Cancel summarize" : "Summarize document"}
                >
                  {isSummarizeActive ? '✓ ' : ''}
                  {isImageFileType(uploadedFiles[0].type) ? 'Describe' : isDataFileType(uploadedFiles[0].type) ? 'Analyze' : 'Summarize'}
                </button>
              )}
              {/* Compare button - only show when exactly 2 text documents are uploaded */}
              {uploadedFiles.length === 2 && uploadedFiles.every(f => isTextDocumentType(f.type)) && (
                <button 
                  type="button" 
                  disabled={isStreaming}
                  className="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded whitespace-nowrap min-w-fit leading-none transition-all" 
                  style={{ 
                    paddingTop: '5px', 
                    paddingBottom: '5px',
                    backgroundColor: isCompareActive ? '#5E95ED' : 'transparent',
                    color: isCompareActive ? 'white' : '#5E95ED',
                    border: isCompareActive ? '2px solid #5E95ED' : '2px solid #5E95ED',
                    opacity: isCompareActive ? 1 : 0.9,
                    fontWeight: isCompareActive ? '600' : '500'
                  }}

                  onClick={() => {
                    // Toggle Compare mode
                    if (isCompareActive) {
                      // Deactivate: clear prompt and reset state
                      setIsCompareActive(false);
                      setValue('');
                    } else {
                      // Activate: set prompt and mark as active
                      setIsCompareActive(true);
                      setIsSummarizeActive(false);
                      setValue('Compare these two files and highlight the key differences.');
                      if (onExternalValueApplied) onExternalValueApplied();
                    }
                  }}
                  aria-label={isCompareActive ? "Cancel comparison" : "Compare documents"}
                >
                  {isCompareActive ? '✓ Compare' : 'Compare'}
                </button>
              )}
              
              {uploadedFiles.length > 1 && (
                <>
                  <button type="button" className="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded bg-[var(--color-accent)] text-white hover:opacity-80 whitespace-nowrap leading-none" style={{ paddingTop: '5px', paddingBottom: '5px' }} onClick={handleClearAllFiles} aria-label="Clear all files">Clear</button>
                </>
              )}
                  </div>
                )}
              </div>
            )}
          </div>
          
        {/* Action buttons container - positioned on the right side with proper spacing */}
        {selectedTool !== 'translation' && (<div className="absolute right-4 flex items-center gap-0 pl-4" style={{ top: isMobile || isTablet ? '1px' : '8px' }}>

          {/* ── Batch Mode: Cancel (X) + Confirm (✓) buttons while listening ── */}
          {dictationMode === 'batch' && isSpeechListening && !isVoiceModeActive && !voiceModeLoading && (
            <>
              {/* Cancel dictation button (X) */}
              <button
                type="button"
                onClick={cancelDictation}
                aria-label="Cancel dictation"
                title="Cancel dictation"
                className="border-none"
                style={{
                  display: 'flex',
                  width: '40px',
                  height: '40px',
                  padding: '10px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.2s ease',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <X size={20} />
              </button>
              {/* Confirm dictation button (✓) */}
              <button
                type="button"
                onClick={() => confirmDictation()}
                aria-label="Confirm dictation"
                title="Confirm dictation — send for transcription"
                className="border-none"
                style={{
                  display: 'flex',
                  width: '40px',
                  height: '40px',
                  padding: '10px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#5E95ED',
                  transition: 'background-color 0.2s ease',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#4a7fd9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#5E95ED'; }}
              >
                <Check size={20} />
              </button>
            </>
          )}

          {/* ── Batch Mode: Snowflake spinner while transcribing (no X button) ── */}
          {dictationMode === 'batch' && isSpeechTranscribing && !isSpeechListening && !isVoiceModeActive && !voiceModeLoading && (
            <>
              {/* Processing snowflake spinner only — no cancel button during Dictating... */}
              <div
                style={{
                  display: 'flex',
                  width: '40px',
                  height: '40px',
                  padding: '10px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <svg
                  className="dictation-snowflake-spinner"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ color: 'var(--color-text-muted, #9ca3af)' }}
                >
                  {/* 6 spokes */}
                  {[0, 30, 60, 90, 120, 150].map((angle) => (
                    <line
                      key={angle}
                      x1="12"
                      y1="2"
                      x2="12"
                      y2="22"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      transform={`rotate(${angle} 12 12)`}
                    />
                  ))}
                  {/* 12 tip dots */}
                  {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle) => (
                    <circle
                      key={`d-${angle}`}
                      cx="12"
                      cy="2.5"
                      r="1.2"
                      fill="currentColor"
                      transform={`rotate(${angle} 12 12)`}
                    />
                  ))}
                </svg>
              </div>
            </>
          )}

          {/* ── Dictation button (Mic) — shown when NOT actively recording/processing in batch mode ── */}
          {/* In ghost mode OR when idle in batch mode: show the standard mic button */}
          {!isVoiceModeActive && !voiceModeLoading && !(dictationMode === 'batch' && (isSpeechStarting || isSpeechListening || isSpeechTranscribing)) && (
            <button
              type="button"
              onClick={() => isSpeechListening ? stopSpeech() : handleStartSpeech()}
              disabled={isStreaming || isSpeechStarting || (!isSpeechListening && isSpeechTranscribing)}
              aria-label={isSpeechStarting ? 'Starting dictation…' : isSpeechListening ? 'Stop dictation' : isSpeechTranscribing ? 'Transcribing…' : 'Dictate (Ctrl+Shift+D)'}
              title={isSpeechStarting ? 'Starting dictation…' : isSpeechListening ? 'Listening… click to stop' : isSpeechTranscribing ? 'Transcribing…' : 'Dictate (Ctrl+Shift+D)'}
              className={`border-none disabled:opacity-50 disabled:cursor-not-allowed${isSpeechListening ? ' dictation-pulse-ring' : ''}`}
              onMouseEnter={(e) => {
                if (!isStreaming && !isSpeechTranscribing && !isSpeechStarting) {
                  e.currentTarget.style.backgroundColor = isSpeechListening ? '#B0030E' : 'var(--color-hover)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isSpeechListening ? '#D10411' : 'transparent';
              }}
              style={{
                display: 'flex',
                width: '40px',
                height: '40px',
                padding: '10px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: isSpeechListening ? '#D10411' : 'transparent',
                transition: 'background-color 0.2s ease',
                color: isSpeechListening ? '#FFFFFF' : 'var(--color-text)',
                cursor: (isStreaming || isSpeechStarting || (!isSpeechListening && isSpeechTranscribing)) ? 'not-allowed' : 'pointer',
                flexShrink: 0,
              }}
            >
              {isSpeechListening
                ? <MicOff size={20} color="#FFFFFF" />
                : (isSpeechTranscribing || isSpeechStarting)
                  ? <span className="animate-spin" style={{ width: 20, height: 20, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block' }} />
                  : <Mic size={20} />}
            </button>
          )}

          {/* Voice Mode button — visible when idle (no text, not streaming) OR when loading/active */}
          {/* Hidden during batch dictation recording/processing */}
          {!shouldHideTools && !(dictationMode === 'batch' && (isSpeechListening || isSpeechTranscribing)) && (voiceModeLoading || isVoiceModeActive || (!value.trim() && !isStreaming)) && (
            <button
              type="button"
              onClick={handleVoiceModeButtonClick}
              className="border-none transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={(isStreaming && !voiceModeLoading && !isVoiceModeActive) || isSpeechListening}
              aria-label={voiceModeLoading ? "Cancel voice mode" : isVoiceModeActive ? "End voice mode" : "Start voice mode"}
              title={voiceModeLoading ? "Cancel voice mode" : isVoiceModeActive ? "End voice mode" : isStreaming ? "Voice mode unavailable while responding" : "Start voice mode"}
              data-tutorial="voice-mode-button"
              onMouseEnter={(e) => {
                if (!voiceModeLoading && !isVoiceModeActive && !isStreaming) {
                  e.currentTarget.style.backgroundColor = 'var(--color-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (!voiceModeLoading && !isVoiceModeActive) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              style={{
                width: (voiceModeLoading || isVoiceModeActive) ? 'auto' : 'auto',
                height: 'auto',
                minWidth: (voiceModeLoading || isVoiceModeActive) ? (isMobile ? 'auto' : '120px') : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: (voiceModeLoading || isVoiceModeActive) ? (isMobile ? 0 : '8px') : 0,
                lineHeight: 0,
                padding: (voiceModeLoading || isVoiceModeActive) ? (isMobile ? '8px' : '8px 16px') : '8px',
                backgroundColor: (voiceModeLoading || isVoiceModeActive) ? 'var(--color-accent)' : 'transparent',
                color: (voiceModeLoading || isVoiceModeActive) ? 'white' : 'var(--color-text)',
                transition: 'background-color 0.2s ease',
                marginLeft: '2px',
                marginRight: '2px',
                borderRadius: '8px',
                cursor: (isStreaming && !voiceModeLoading && !isVoiceModeActive) ? 'not-allowed' : 'pointer',
              }}
            >
              {voiceModeLoading ? (
                <>
                  <div
                    className="animate-spin rounded-full border-2 border-white border-t-transparent"
                    style={{ width: 16, height: 16}}
                  />
                  {!isMobile && (
                    <span className="text-sm font-medium" style={{ paddingTop: '2px', paddingLeft: '2px' }}>
                      Cancel
                    </span>
                  )}
                </>
              ) : isVoiceModeActive ? (
                <>
                  <AudioWaveform 
                    isActive={isVoiceModeActive} 
                    audioLevel={audioLevel}
                    frequencyData={frequencyData}
                    size={16}
                    color="white"
                  />
                  <span className="text-sm font-medium">End</span>
                </>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AudioLines size={iconSize || 24} />
                </span>
              )}
            </button>
          )}


          {/* Hidden camera input — mobile opens camera app directly (capture=environment) */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ position: 'absolute', left: '-9999px', top: 0, width: '1px', height: '1px', opacity: 0.01 }}
            onClick={() => {
              setAwaitingFileSelection(true);
            }}
            onChange={e => {
              setAwaitingFileSelection(false);
              handleFileChange(e);
            }}
          />

          {/* Hidden image input — mobile opens gallery/photo picker */}
          <input
            ref={mobileImageInputRef}
            type="file"
            accept="image/png,image/jpeg,.png,.jpg,.jpeg"
            multiple
            style={{ position: 'absolute', left: '-9999px', top: 0, width: '1px', height: '1px', opacity: 0.01 }}
            onClick={() => {
              setAwaitingFileSelection(true);
            }}
            onChange={e => {
              setAwaitingFileSelection(false);
              handleFileChange(e);
            }}
          />

          {/* Hidden file input for actually uploading the file */}
          <input
            data-testid="chat-file-input"
            ref={fileInputRef}
            type="file"
            multiple
            // Dynamic accept: restricts to same file type as already uploaded files
            accept={getAllowedFileTypes}
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '1px',
              height: '1px',
              opacity: 0.01,
            }}
            onClick={() => {
              filesBeingProcessedRef.current = false;
              setAwaitingFileSelection(true);
            }}
            onChange={e => {
              setAwaitingFileSelection(false);
              handleFileChange(e);
            }}
          />

          {/* Send/Stop button — replaces voice agent icon when text is present or streaming */}
          {/* In 3M Knowledge mode (shouldHideTools), send button is always visible (voice mode hidden) */}
          {/* Hidden during batch dictation listening/transcribing — confirm (✓) replaces it */}
          {(!!value.trim() || isStreaming || shouldHideTools) && !(dictationMode === 'batch' && (isSpeechListening || isSpeechTranscribing || isSpeechStarting)) && (
          <button
            type="button"
            onClick={() => {
              logger.log('[ChatInput] Stop/Send button clicked', { isStreaming, hasOnStopStream: !!onStopStream });
              if (isStreaming) {
                logger.log('[ChatInput] Calling onStopStream');
                onStopStream?.();
              } else {
                handleSendClick();
              }
            }}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={(e) => {
              if (value.trim() && !isSpeechListening) {
                e.currentTarget.style.backgroundColor = '#4a7fd9';
              }
            }}
            onMouseLeave={(e) => {
              if (value.trim() && !isSpeechListening) {
                e.currentTarget.style.backgroundColor = '#5E95ED';
              }
            }}
            className="border-none transition text-[var(--color-text)] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSpeechListening && !isStreaming}
            aria-label={isStreaming ? "Stop" : "Send"}
            title={isStreaming ? "Stop" : "Send"}
            style={{
              width: 'auto',
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '4px',
              marginRight: '4px',
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: (value.trim() && !isSpeechListening) ? '#5E95ED' : 'transparent',
              transition: 'background-color 0.2s ease',
            }}
          >
            {(isStreaming && !isImageStreaming) ? (
              <span style={{ width: iconSize || 24, height:  iconSize || 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <StopIcon size={iconSize || 24} color="#D10411" />
              </span>
            ) : isStreaming ? ( <></> ) : (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PaperPlaneIcon
                  className="transition-colors"
                  width={iconSize || 20}
                  height={iconSize || 20}
                />
              </span>
            )}
          </button>
          )}
        </div>)}

          {/* Unified file warning dialog — handles both general/analysis and translation mode warnings */}
          {fileWarning && (fileWarning.mode === 'translation' || !isFileUploadDialogOpen) && (
            <FileSelectionWarningDialog
              title={fileWarning.title}
              subtitle={fileWarning.mode === 'translation'
                ? (fileWarning.subtitle !== undefined ? fileWarning.subtitle : 'The selected file could not be added to your translation.')
                : undefined}
              message={fileWarning.message}
              zIndex={19000}
              onClose={() => {
                setFileWarning(null);
                if (fileWarning.mode === 'general' && selectedTool === 'data_analysis' && uploadedFiles.length === 0) {
                  setIsFileUploadDialogOpen(true);
                }
              }}
            />
          )}

      {/* Custom blocking message for drag-drop attempts during spinner/streaming */}
      {showBlockingMessage && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          style={{ zIndex: 19500 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowBlockingMessage(false);
          }}
        >
          <div
            className="flex flex-col gap-3 p-4 rounded-md border border-[var(--color-accent)] bg-[var(--color-secondary)] text-[var(--color-text)] shadow-xl max-w-sm w-full mx-4"
            onMouseDown={(e) => e.stopPropagation()}
            role="alert"
            aria-live="assertive"
            aria-label="Action blocked message"
          >
            <div className="flex items-start gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="1" />
              </svg>
              <span className="text-sm font-medium">Action not allowed in current state. Please try uploading later.</span>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1 text-xs rounded bg-[var(--color-accent)] text-white hover:opacity-80"
                onClick={() => setShowBlockingMessage(false)}
              >OK</button>
            </div>
          </div>
        </div>,
        document.body
      )}

        {/* File Upload Dialog */}
        <FileUploadDialog
          isOpen={isFileUploadDialogOpen}
          onClose={handleFileUploadDialogClose}
          onUpload={handleFileUploadDialogUpload}
          onDropzoneClick={handleFileUploadDialogDropzoneClick}
          uploadedFiles={stagedFiles}
          isUploading={stagedFiles.some(f => f.status === 'uploading')}
          isDataAnalysisMode={selectedTool === 'data_analysis'}
          onRemoveFile={handleRemoveStagedFile}
          onCancelFileRead={handleFileUploadDialogCancelFileRead}
          awaitingFileSelection={awaitingFileSelection}
          fileWarning={isFileUploadDialogOpen && fileWarning?.mode === 'general' ? fileWarning.message : null}
          fileWarningTitle={isFileUploadDialogOpen && fileWarning?.mode === 'general' ? (fileWarning.title ?? null) : null}
          onClearFileWarning={() => setFileWarning(null)}
          onFilesDropped={handleFilesDroppedOnDialog}
          hideDropzone={hideFileUploadDialogDropzone}
        />

        {/* Microphone Selector Dialog - MOVED outside motion.form; see below */}
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      {confirmationConfig && (
        <ConfirmationDialog
          isOpen={showConfirmation}
          title={confirmationConfig.title}
          message={confirmationConfig.message}
          onConfirm={confirmationConfig.onConfirm}
          onCancel={() => {
            setShowConfirmation(false);
            setConfirmationConfig(null);
          }}
          confirmText={confirmationConfig.confirmText}
        />
      )}

      {/* Mobile Tools Menu Modal */}
      {isMobile && isMobileToolsMenuOpen && !isMobileAiModelPanelOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          onClick={() => setIsMobileToolsMenuOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" />

          {/* Menu drawer from bottom */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="relative w-full bg-[var(--color-secondary)] rounded-t-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-base font-semibold text-[var(--color-text)]">Source</h3>
              <button
                type="button"
                onClick={() => setIsMobileToolsMenuOpen(false)}
                className="text-[var(--color-text)] opacity-70 hover:opacity-100"
                aria-label="Close menu"
              >
                <Cross2Icon width={20} height={20} />
              </button>
            </div>
            <div className="border-t mx-5" style={{ borderColor: 'var(--color-border)' }} />

            {/* Tool toggles list */}
            <div className="px-2 py-2 max-h-[50vh] overflow-y-auto">
              {/* File Upload row */}
              {!shouldHideTools && isFileUploadSupported && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileToolsMenuOpen(false);
                      triggerAdditionalUpload();
                    }}
                    className="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-150 active:bg-[var(--color-primary)]"
                  >
                    <span className="flex items-center justify-center w-7 h-7 flex-shrink-0 text-[var(--color-text)]">
                      <FileUploadIconSVG width={22} height={22} style={{ color: 'var(--color-text)' }} />
                    </span>
                    <span className="flex-1 text-left mt-1 text-sm font-normal text-[var(--color-text)]">File Upload</span>
                  </button>
                </>
              )}

              {chatOptions.filter(toolOption => toolOption.apiLabel !== 'reasoning' && toolOption.apiLabel !== 'image_generation').map((toolOption) => {
                const IconComponent = toolOption.icon as React.ComponentType<any>;
                const isActive = selectedTool === toolOption.apiLabel;
                const isImageGenDisabled = isImageStreaming && !error && toolOption.apiLabel === 'image_generation';
                const isDisabled = isImageGenDisabled;

                return (
                  <React.Fragment key={toolOption.apiLabel}>
                  <button
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      if (isActive) {
                        handleRemoveTool();
                        setIsMobileToolsMenuOpen(false);
                      } else {
                        handleOptionClick(toolOption.apiLabel);
                      }
                    }}
                    className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-150 ${
                      isDisabled ? 'opacity-40 cursor-not-allowed' : 'active:bg-[var(--color-primary)]'
                    }`}
                  >
                    {/* Icon */}
                    <span className="flex items-center justify-center w-7 h-7 flex-shrink-0 text-[var(--color-text)]">
                      <IconComponent width={22} height={22} size={22} style={{ display: 'block' }} />
                    </span>

                    {/* Label */}
                    <span className="flex-1 text-left mt-1 text-sm font-normal text-[var(--color-text)]">
                      {toolOption.label}
                      {isDisabled && toolOption.apiLabel === 'image_generation' && (
                        <span className="ml-1 text-xs opacity-60">(Generating…)</span>
                      )}
                    </span>

                    {/* Toggle switch */}
                    <span
                      className="relative inline-flex flex-shrink-0 items-center"
                      style={{ width: 38, height: 18 }}
                      aria-checked={isActive}
                      role="switch"
                    >
                      <span
                        style={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: 999,
                          backgroundColor: isActive ? '#2664D3' : 'var(--color-accent)',
                          opacity: isActive ? 1 : 0.35,
                          transition: 'background-color 0.2s ease',
                        }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: isActive ? 22 : 2,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                          transition: 'left 0.2s ease',
                        }}
                      />
                    </span>
                  </button>
                  </React.Fragment>
                );
              })}

              {/* AI Model row — only visible when model selector is applicable */}
              {isModelSelectorVisible(selectedTool) && (
                <button
                  type="button"
                  onClick={() => setIsMobileAiModelPanelOpen(true)}
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-150 active:bg-[var(--color-primary)]"
                >
                  <span className="flex items-center justify-center w-7 h-7 flex-shrink-0 text-[var(--color-text)]">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  </span>
                  <span className="flex-1 text-left mt-1 text-sm font-normal text-[var(--color-text)]">AI Model</span>
                  <span className="relative inline-flex flex-shrink-0 items-center justify-center" style={{ width: 38, height: 18 }}>
                    <ChevronRightIcon width={24} height={24} style={{ color: 'var(--color-text)', opacity: 0.6 }} />
                  </span>
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* AI Model Sub-Panel */}
      {isMobile && isMobileAiModelPanelOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          onClick={() => setIsMobileAiModelPanelOpen(false)}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="relative w-full bg-[var(--color-secondary)] rounded-t-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-base font-semibold text-[var(--color-text)]">AI Model</h3>
              <button
                type="button"
                onClick={() => setIsMobileAiModelPanelOpen(false)}
                className="text-[var(--color-text)] opacity-70 hover:opacity-100"
                aria-label="Close AI Model panel"
              >
                <Cross2Icon width={20} height={20} />
              </button>
            </div>
            <div className="px-2 py-2 pb-8">
              {getVisibleModelOptions(selectedTool).map((model: ModelConfig) => {
                const isSelected = model.id === chatModeState.selectedModelId;
                return (
                  <button
                    key={model.id}
                    type="button"
                    onClick={() => {
                      handleModelSelection(model.id);
                      setIsMobileAiModelPanelOpen(false);
                      setIsMobileToolsMenuOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-all duration-150 active:bg-[var(--color-primary)]"
                    style={{ color: isSelected ? '#5E95ED' : 'var(--color-text)' }}
                  >
                    <span>{model.displayName}</span>
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E95ED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
      {/* Dictation Error Dialog */}
      <ConfirmationDialog
        isOpen={showDictationError}
        title="Unable to Dictate"
        message="We're sorry, we're unable to dictate at the moment. Please try again later."
        onConfirm={() => setShowDictationError(false)}
        onCancel={() => setShowDictationError(false)}
        confirmText="OK"
        showCancelButton={false}
      />

      {/* Voice Mode Error Dialog */}
      <ConfirmationDialog
        isOpen={showVoiceModeError}
        title="Error: Could not connect to Voice Mode"
        message={voiceModeErrorMessage}
        onConfirm={() => setShowVoiceModeError(false)}
        onCancel={() => setShowVoiceModeError(false)}
        confirmText="OK"
        showCancelButton={false}
      />

      {/* Microphone Permission Dialog - only shown when permission is denied */}
      <MicrophonePermissionDialog
        isOpen={showMicPermissionDialog}
        onClose={() => setShowMicPermissionDialog(false)}
      />

    </motion.form>
      )}

      {/* No Audio Detected Dialog - state lives in Redux so it survives navigation/remounts */}
      <ConfirmationDialog
        isOpen={showNoAudioDialog}
        title="No Audio Detected"
        message="We're not detecting any sound from your microphone. Would you like to switch to a different microphone?"
        onConfirm={handleNoAudioDialogConfirm}
        onCancel={handleNoAudioDialogDismiss}
        confirmText="Switch Microphone"
        cancelText="Continue Anyway"
      />

      {/* Microphone Selector Dialog - outside motion.form so it renders even when voice mode hides the form */}
      <MicrophoneSelectorDialog
        isOpen={showMicSelector}
        onClose={() => setShowMicSelector(false)}
        onMicrophoneSelected={handleMicrophoneSelected}
      />

      {/* Inactivity warning dialog - rendered at ChatInput level so it persists after voice mode ends */}
      <InactivityWarningDialog 
        isOpen={showInactivityWarning}
        countdown={inactivityCountdown}
        disconnectedByInactivity={disconnectedByInactivity}
        onDismiss={handleDismissInactivityWarning}
      />
    </>
  );
}

export {};